import React from 'react';
import { clsx } from 'clsx';

interface ProgressBarProps {
  id?: string;
  /** Completed fraction, 0-1. Null draws an indeterminate bar: work under way
   *  with nothing to count yet. */
  value: number | null;
  /** Accessible name of the bar. */
  label: string;
  /** What a screen reader says for the value, e.g. "120,000 of 285,000 rows". */
  valueText?: string;
  className?: string;
}

/**
 * Thin progress bar (LR-728). A real `progressbar` - value, range and spoken
 * text - rather than a decorative stripe, because the number it draws is the
 * only sign a long export is moving.
 *
 * Determinate: the width eases over a second, the interval the export reports
 * on, so the bar crawls forward instead of jumping between readings.
 *
 * Indeterminate: a segment sliding across the track. Under
 * prefers-reduced-motion nothing may move, so it falls back to a dimmed full
 * track - dimmed to 70%, which still stands 3:1 off the page (WCAG 1.4.11) -
 * rather than a filled bar that would read as finished.
 */
export const ProgressBar: React.FC<ProgressBarProps> = ({
  id,
  value,
  label,
  valueText,
  className,
}) => {
  const percent =
    value === null ? null : Math.round(Math.min(Math.max(value, 0), 1) * 100);
  return (
    <div
      id={id}
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent ?? undefined}
      aria-valuetext={valueText}
      className={clsx(
        'h-1 overflow-hidden rounded-full bg-lr-border',
        className
      )}
    >
      <div
        className={clsx(
          'h-full rounded-full bg-lr-accent',
          percent === null
            ? 'w-full bg-lr-accent/70 motion-safe:w-1/3 motion-safe:animate-progress-slide motion-safe:bg-lr-accent'
            : 'motion-safe:transition-[width] motion-safe:duration-1000 motion-safe:ease-linear'
        )}
        style={percent === null ? undefined : { width: `${percent}%` }}
      />
    </div>
  );
};
